import { useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@scripsteam/scrips-signal-ds';
import { TextField } from '@scripsteam/scrips-signal-ds';
import { cn } from '@/lib/utils';
import { FieldLabel } from '@scripsteam/scrips-signal-ds';
import { useChangePassword } from '@/settings/services';

/**
 * Self-service password change — current / new (with the 5-rule checklist) /
 * confirm + Update. Mirrors Flutter `password_widget.dart`.
 *
 * Shared by the org Account Details page (PROD-755) and the Account Settings
 * self-edit account tabs (Flutter `isFromAccountSettings` layout) so the
 * rule list + validation live in one place. Posts via `useChangePassword`
 * (PUT /api/identity-server/Account/ChangePassword/{userId}).
 */
export function PasswordChangeForm({ userId }: { userId: string | undefined }) {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const changePassword = useChangePassword();

  const rules = passwordRules(next);
  const allRulesPass = Object.values(rules).every(Boolean);
  const passwordsMatch = next.length > 0 && next === confirm;
  const isValid = !!current && allRulesPass && passwordsMatch;
  // Design PaSetPasswordCard `error` (nurse-account-mismatch): said once Confirm holds something that differs.
  const showMismatch = confirm.length > 0 && !passwordsMatch;

  const handleUpdate = () => {
    if (!userId || !isValid) return;
    changePassword.mutate(
      { userId, currentPassword: current, newPassword: next, confirmPassword: confirm },
      {
        onSuccess: () => {
          toast.success('Password updated. You may be logged out shortly.');
          setCurrent('');
          setNext('');
          setConfirm('');
        },
      },
    );
  };

  return (
    <div className="space-y-4">
      <div>
        <FieldLabel stacked>Current password</FieldLabel>
        <TextField
          type="password"
          value={current}
          onChange={(e) => setCurrent(e.target.value)}
          placeholder="Enter current password"
          autoComplete="current-password"
        />
      </div>
      <div>
        <FieldLabel stacked>New password</FieldLabel>
        <TextField
          type="password"
          value={next}
          onChange={(e) => setNext(e.target.value)}
          placeholder="Enter new password"
          autoComplete="new-password"
        />
        <ul className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
          <RuleItem ok={rules.length}>At least 8 characters</RuleItem>
          <RuleItem ok={rules.number}>1 number</RuleItem>
          <RuleItem ok={rules.lower}>1 lowercase</RuleItem>
          <RuleItem ok={rules.upper}>1 uppercase character</RuleItem>
          <RuleItem ok={rules.special}>1 special character (@, _, !, $ or %)</RuleItem>
        </ul>
      </div>
      <div className="flex items-end gap-3">
        <div className="flex-1">
          <FieldLabel stacked>Confirm new password</FieldLabel>
          <TextField
            type="password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            placeholder="Confirm new password"
            autoComplete="new-password"
          />
          {showMismatch && (
            <p data-slot="password-mismatch" role="alert" className="mt-1 text-sm text-status-error">
              Passwords do not match
            </p>
          )}
        </div>
        <Button
          type="button"
          onClick={handleUpdate}
          disabled={!isValid || changePassword.isPending}
        >
          {changePassword.isPending ? 'Updating…' : 'Update'}
        </Button>
      </div>
    </div>
  );
}

function RuleItem({ ok, children }: { ok: boolean; children: React.ReactNode }) {
  return (
    <li
      className={cn(
        'flex items-center',
        ok
          ? 'text-status-success'
          : 'text-text-muted',
      )}
    >
      <span className="mr-2">•</span>
      {children}
    </li>
  );
}

function passwordRules(pw: string) {
  return {
    length: pw.length >= 8,
    number: /[0-9]/.test(pw),
    lower: /[a-z]/.test(pw),
    upper: /[A-Z]/.test(pw),
    special: /[@_!$%]/.test(pw),
  };
}
